import { Tab, Tabs } from "@mui/material";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { DEFAULT_PROFILE_TAB, isProfileTab, PROFILE_TAB_ID, PROFILE_TAB_PANEL_ID, resolveVisibleProfileTab, type ProfileTab } from "./profileTabs";
import { useUserProfileStrings } from "./userProfile-strings";

type AccountTabsProps = {
    value: ProfileTab;
    onChange: (tab: ProfileTab) => void;
};

function getMobileTabsScroller(wrap: HTMLElement | null): HTMLElement | null {
    return wrap?.querySelector<HTMLElement>(".MuiTabs-scroller") ?? null;
}

function tabScrollBehavior(): ScrollBehavior {
    if (typeof window === "undefined") {
        return "auto";
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

function isScrollableOverflowY(overflowY: string): boolean {
    return overflowY === "auto" || overflowY === "scroll";
}

/** Nearest scroll container for account tab stickiness (content-column, #app, or viewport). */
function accountTabsScrollRoot(el: HTMLElement | null): Element | null {
    let node = el?.parentElement ?? null;
    while (node) {
        const { overflowY } = window.getComputedStyle(node);
        if (isScrollableOverflowY(overflowY)) {
            return node;
        }
        node = node.parentElement;
    }

    const contentColumn = el?.closest("#orderApp .content-column");
    if (contentColumn instanceof HTMLElement) {
        const { overflowY } = window.getComputedStyle(contentColumn);
        if (isScrollableOverflowY(overflowY)) {
            return contentColumn;
        }
    }

    const app = document.getElementById("app");
    if (app) {
        const { overflowY } = window.getComputedStyle(app);
        if (isScrollableOverflowY(overflowY)) {
            return app;
        }
    }

    return null;
}

export default function AccountTabs({ value, onChange }: AccountTabsProps) {
    const profileStrings = useUserProfileStrings();
    const activeTab = resolveVisibleProfileTab(isProfileTab(value) ? value : DEFAULT_PROFILE_TAB);
    const isMobileTabs = useMediaQuery({ query: "(max-width: 930px)" });
    const scrollWrapRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);
    const stickySentinelRef = useRef<HTMLDivElement>(null);
    const [mobileScrollHints, setMobileScrollHints] = useState({ left: false, right: false });
    const [tabsStuck, setTabsStuck] = useState(false);

    const updateMobileScrollHints = useCallback((): void => {
        const scroller = getMobileTabsScroller(scrollWrapRef.current);
        if (!scroller) {
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = scroller;
        const maxScrollLeft = scrollWidth - clientWidth;
        setMobileScrollHints({
            left: scrollLeft > 2,
            right: maxScrollLeft > 2 && scrollLeft < maxScrollLeft - 2,
        });
    }, []);

    const centerSelectedTab = useCallback((): void => {
        const wrap = scrollWrapRef.current;
        const scroller = getMobileTabsScroller(wrap);
        if (!wrap || !scroller) {
            return;
        }

        const selectedTab = wrap.querySelector<HTMLElement>(".MuiTab-root.Mui-selected");
        if (!selectedTab) {
            return;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const tabRect = selectedTab.getBoundingClientRect();
        const tabLeftInScroll = scroller.scrollLeft + (tabRect.left - scrollerRect.left);
        const targetScrollLeft = tabLeftInScroll + tabRect.width / 2 - scroller.clientWidth / 2;
        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;

        scroller.scrollTo({
            left: Math.max(0, Math.min(targetScrollLeft, maxScrollLeft)),
            behavior: tabScrollBehavior(),
        });
        updateMobileScrollHints();
    }, [updateMobileScrollHints]);

    useLayoutEffect(() => {
        if (!isMobileTabs) {
            return undefined;
        }

        const runCenter = (): void => {
            centerSelectedTab();
        };

        runCenter();
        const rafId = requestAnimationFrame(runCenter);
        const timeoutId = window.setTimeout(runCenter, 150);

        let resizeObserver: ResizeObserver | undefined;
        const scroller = getMobileTabsScroller(scrollWrapRef.current);
        if (scroller) {
            resizeObserver = new ResizeObserver(() => {
                runCenter();
                updateMobileScrollHints();
            });
            resizeObserver.observe(scroller);
            scroller.addEventListener("scroll", updateMobileScrollHints, { passive: true });
            updateMobileScrollHints();
        }

        return () => {
            cancelAnimationFrame(rafId);
            window.clearTimeout(timeoutId);
            resizeObserver?.disconnect();
            scroller?.removeEventListener("scroll", updateMobileScrollHints);
        };
    }, [activeTab, isMobileTabs, centerSelectedTab, updateMobileScrollHints]);

    useLayoutEffect(() => {
        const sentinel = stickySentinelRef.current;
        const stickyEl = stickyRef.current;
        if (!sentinel || !stickyEl) {
            return undefined;
        }

        let observer: IntersectionObserver | undefined;

        const bindStuckObserver = (): void => {
            observer?.disconnect();
            const scrollRoot = accountTabsScrollRoot(stickyEl);
            observer = new IntersectionObserver(
                ([entry]) => setTabsStuck(!entry.isIntersecting),
                { root: scrollRoot, threshold: 0 },
            );
            observer.observe(sentinel);
        };

        bindStuckObserver();

        const onLayoutChange = (): void => {
            bindStuckObserver();
        };

        window.addEventListener("resize", onLayoutChange, { passive: true });
        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", onLayoutChange);
        };
    }, [isMobileTabs, activeTab]);

    const tabs = (
        <Tabs
            value={activeTab}
            onChange={(_, tab: ProfileTab) => onChange(tab)}
            variant={isMobileTabs ? "scrollable" : "fullWidth"}
            scrollButtons={false}
            allowScrollButtonsMobile={false}
            sx={{
                minHeight: 47,
                mb: 0,
                "& .MuiTabs-flexContainer": {
                    minHeight: 47,
                },
                "& .MuiTabs-indicator": {
                    height: 3,
                    backgroundColor: "var(--lb_green)",
                },
                "& .MuiTab-root": {
                    minHeight: 47,
                    py: 0,
                    px: 1,
                    textTransform: "none",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "20px",
                    color: "var(--lb_on_surface_variant)",
                    flex: isMobileTabs ? "0 0 auto" : undefined,
                    maxWidth: isMobileTabs ? "none" : undefined,
                    whiteSpace: isMobileTabs ? "nowrap" : undefined,
                    paddingLeft: isMobileTabs ? "12px" : undefined,
                    paddingRight: isMobileTabs ? "12px" : undefined,
                    "&.Mui-selected": {
                        color: "var(--lb_green)",
                    },
                },
                borderBottom: "1px solid var(--divider_white)",
            }}
        >
            <Tab value="personal" id={PROFILE_TAB_ID.personal} aria-controls={PROFILE_TAB_PANEL_ID.personal} label={profileStrings.tabPersonalInfo} />
            <Tab value="orders" id={PROFILE_TAB_ID.orders} aria-controls={PROFILE_TAB_PANEL_ID.orders} label={profileStrings.tabOrderHistory} />
            <Tab value="transactions" id={PROFILE_TAB_ID.transactions} aria-controls={PROFILE_TAB_PANEL_ID.transactions} label={profileStrings.tabTransactionHistory} />
        </Tabs>
    );

    const wrapClassName = [
        "account-tabs-mobile-wrap",
        mobileScrollHints.left ? "account-tabs-mobile-wrap--scroll-left" : "",
        mobileScrollHints.right ? "account-tabs-mobile-wrap--scroll-right" : "",
    ].filter(Boolean).join(" ");

    const tabsBar = isMobileTabs ? (
        <div ref={scrollWrapRef} className={wrapClassName}>
            {tabs}
        </div>
    ) : tabs;

    const stickyClassName = [
        "account-tabs-sticky",
        tabsStuck ? "account-tabs-sticky--stuck" : "",
    ].filter(Boolean).join(" ");

    return (
        <>
            <div ref={stickySentinelRef} className="account-tabs-sticky-sentinel" aria-hidden="true" />
            <div ref={stickyRef} className={stickyClassName}>
                {tabsBar}
            </div>
        </>
    );
}
